import React, { useEffect, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import DraftEditor from './DraftEditor'; // Assuming you have a Draft.js Editor component
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import {
    Typography,
    IconButton,
    Grid,
    TextareaAutosize,
    MenuItem,
    Tabs,
    Tab,
    Box,
    TableContainer,
    Table,
    Paper,
    TableHead,
    TableRow,
    TableCell,
    TableSortLabel,
    Snackbar,
    Alert,
    Card,
    CardContent,
    Divider,
    Chip,
    useTheme,
    useMediaQuery,
    Container,
    Stack,
    Fab,
    Fade,
    Slide
} from '@mui/material';
import { addAssignment, addDropDownData, getDropdownData } from '../Queries';
import Interpretations from './Interpretations';
import Recommendations from './Recomendations';

const AssessmentForm = ({ assessment }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    const [questions, setQuestions] = useState([{ id: new Date().getTime().toString(), question: '', options: [], notes: '', optionType: 'Radio Button' }]);
    const [newOption, setNewOption] = useState('');
    const [assignedTo, setAssignedTo] = useState([]); // For the Assigned To field
    const [assessmentType, setAssessmentType] = useState(''); // For the assessment type dropdown
    const [showPopup, setShowPopup] = useState(false);
    const [dataType, setDataType] = useState('Question'); // For the data type dropdown in the popup
    const [dropdownQuestions, setDropdownQuestions] = useState([]);
    const [dropdownOptions, setDropdownOptions] = useState([]);
    const [dropdownAssessmentTypes, setDropdownAssessmentTypes] = useState([]);
    const [dropdownProgramCodes, setDropdownProgramCodes] = useState([]);
    const currentUser = localStorage.getItem("user");
    const [description, setDescription] = useState(assessment ? assessment.instructions : '');
    const [reportDescription, setReportDescription] = useState(''); // Report description for dashboard reports
    const [assessmentName, setAssessmentName] = useState('');
    const [formId, setFormId] = useState(new Date().getTime());
    const [submittingMasterData, setSubmittingMasterData] = useState(false);
    const [submittingForm, setSubmittingForm] = useState(false);
    const [score, setScore] = useState(0);
    const [activeTab, setActiveTab] = useState(0); // State for active tab
    const [optionValues, setOptionValues] = useState(['']);
    const [optionScores, setOptionScores] = useState(['']);
    const [interpretations, setInterpretations] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [maxScore, setMaxScore] = useState(0); // Maximum score for the assessment
    const [startDate, setStartDate] = useState(''); // Start date
    const [endDate, setEndDate] = useState(''); // End date
    const [timeLimit, setTimeLimit] = useState(0); // Time limit in minutes

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleAddQuestion = () => {
        const lastQuestion = questions[questions.length - 1];
        if (!lastQuestion.question.trim()) {
            setErrorMessage('Please fill out the previous question before adding a new one.');
            return;
        }
        setQuestions([...questions, { id: new Date().getTime().toString(), question: '', options: [], notes: '', optionType: 'Radio Button' }]);
    };

    const handleQuestionChange = (index, newValue) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index].question = newValue;
        setQuestions(updatedQuestions);
    };

    const handleOptionChange = (index, newValues) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index].options = newValues;
        setQuestions(updatedQuestions);
    };

    const handleNotesChange = (index, newValue) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index].notes = newValue;
        setQuestions(updatedQuestions);
    };

    const handleOptionTypeChange = (index, newValue) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index].optionType = newValue;
        
        // Clear options when switching to Text Field type
        if (newValue === 'Text Field') {
            updatedQuestions[index].options = [];
        }
        
        setQuestions(updatedQuestions);
    };

    const handleAssignedToChange = (newValue) => {
        setAssignedTo(newValue);
    };

    const handleAssessmentTypeChange = (newValue) => {
        setAssessmentType(newValue);
    };

    const handleDataTypeChange = (event) => {
        setDataType(event.target.value);
    };

    const handleDeleteQuestion = (index) => {
        const updatedQuestions = [...questions];
        updatedQuestions.splice(index, 1);
        setQuestions(updatedQuestions);
    };

    const handleOptionValueChange = (index, newValue) => {
        const updatedValues = [...optionValues];
        updatedValues[index] = newValue;
        setOptionValues(updatedValues);
    };

    const handleOptionScoreChange = (index, newValue) => {
        const updatedScores = [...optionScores];
        updatedScores[index] = newValue;
        setOptionScores(updatedScores);
    };

    const handleAddOption = (index) => {
        const newValue = optionValues[index];
        
        // Prevent adding empty options
        if (!newValue || !newValue.trim()) {
            setErrorMessage('Please enter an option text before adding');
            return;
        }
        
        const updatedQuestions = [...questions];
        const newScore = optionScores[index] || "0"; // Default to 0 if no score entered
        updatedQuestions[index].options.push(`${newValue} (Score: ${newScore})`);
        setQuestions(updatedQuestions);
        handleOptionValueChange(index, "");
        handleOptionScoreChange(index, "");
    };

    const handleCloseError = () => {
        setErrorMessage('');
    };

    const handlePopupSubmit = async (event) => {
        event.preventDefault();
        setSubmittingMasterData(true);
        let answer = "";
        if (dataType === "assessment-option") {
            answer = `${newOption} (Score: ${score})`
        }
        try {
            let formData = {
                name: answer ? answer : newOption,
                data_type: dataType,
                email: currentUser
            }
            let response = await addDropDownData(formData, false);
            if (response.data && response.data.statusCode === 200) {
                let result = JSON.parse(response.data.body);
                getDropDowns();
                setNewOption('');
                setSubmittingMasterData(false);
            }
        }
        catch (error) {
            setSubmittingMasterData(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        // Basic validation
        if (!assessmentName || !assessmentType || !assignedTo.length || !maxScore || !startDate || !endDate) {
            setErrorMessage('Please fill all required fields (Assessment Name, Template, Assigned To, Maximum Score, Start Date, and End Date)');
            setSubmittingForm(false);
            return;
        }
        
        // Validate max score is greater than 0
        if (parseInt(maxScore) <= 0) {
            setErrorMessage('Maximum Score must be greater than 0');
            setSubmittingForm(false);
            return;
        }
        
        // Validate end date is not before start date
        if (new Date(endDate) < new Date(startDate)) {
            setErrorMessage('End Date cannot be before Start Date');
            setSubmittingForm(false);
            return;
        }
        
        // Validate questions
        const invalidQuestions = questions.filter((question, index) => {
            // Check if question text is empty
            if (!question.question.trim()) {
                return true;
            }
            // For Radio Button and Checkbox types, require at least one option
            if (question.optionType !== 'Text Field' && (!question.options || question.options.length === 0)) {
                return true;
            }
            return false;
        });

        if (invalidQuestions.length > 0) {
            setErrorMessage('Please ensure all questions have text and Radio Button/Checkbox questions have at least one option');
            setSubmittingForm(false);
            return;
        }
        
        setSubmittingForm(true);
        if (assessment) {
            try {
                let jsonData = {
                    // interpretations: interpretations,
                    // recommendations: recommendations,
                    // questions: questions,
                    max_score: maxScore || 0, // Include maximum score in JSON
                    start_date: startDate || '', // Include start date in JSON
                    end_date: endDate || '', // Include end date in JSON
                    time_limit: timeLimit || 0, // Include time limit in JSON
                    report_description: reportDescription || '', // Include report description in JSON
                };
                
                let formData = {
                    id: assessment ? assessment.form_id : formId.toString(),
                    assignment_name: assessmentName,
                    interpretations: JSON.stringify(interpretations),
                    recommendations: JSON.stringify(recommendations),
                    assignment_type: assessmentType,
                    program_code: assignedTo.toString(),
                    start_date: startDate || '',
                    end_date: endDate || '',
                    questions: JSON.stringify(questions),
                    instructions: description,
                    json: JSON.stringify(jsonData), // Store max_score in JSON field
                    updated_at: new Date().toLocaleDateString().toString(),
                }
                let response = await addAssignment(formData, true, true);
                if (response.data && response.data.statusCode === 200) {
                    const result = JSON.parse(response.data.body);
                    setSuccessMessage(assessment ? 'Assessment updated successfully!' : 'Assessment created successfully!');
                    clearForm();
                } else {
                    throw new Error(response.data?.body || 'Failed to save assessment');
                }
            } catch (error) {
                console.error('Submission error:', error);
                setErrorMessage(error.message || 'Failed to save assessment. Please try again.');
            } finally {
                setSubmittingForm(false);
            }
        }
        else {
            try {
                let jsonData = {
                    // interpretations: interpretations,
                    // recommendations: recommendations,
                    // questions: questions,
                    max_score: maxScore || 0, // Include maximum score in JSON
                    start_date: startDate || '', // Include start date in JSON
                    end_date: endDate || '', // Include end date in JSON
                    time_limit: timeLimit || 0, // Include time limit in JSON
                    report_description: reportDescription || '', // Include report description in JSON
                };
                
                let formData = {
                    id: assessment ? assessment.id : formId.toString(),
                    assignment_name: assessmentName,
                    assignment_type: assessmentType,
                    interpretations: JSON.stringify(interpretations),
                    recommendations: JSON.stringify(recommendations),
                    program_code: assignedTo.toString(),
                    assigned_to: '',
                    assignment_topic: 'assessment',
                    start_date: startDate || '',
                    end_date: endDate || '',
                    questions: JSON.stringify(questions),
                    fileName: "",
                    json: JSON.stringify(jsonData), // Store max_score in JSON field
                    instructions: description,
                    created_at: new Date().toLocaleDateString().toString(),
                    updated_at: new Date().toLocaleDateString().toString(),
                    created_by: currentUser,
                }
                let response = await addAssignment(formData, false);
                if (response.data && response.data.statusCode === 200) {
                    const result = JSON.parse(response.data.body);
                    setSuccessMessage(assessment ? 'Assessment updated successfully!' : 'Assessment created successfully!');
                    clearForm();
                } else {
                    throw new Error(response.data?.body || 'Failed to save assessment');
                }
            } catch (error) {
                console.error('Submission error:', error);
                setErrorMessage(error.message || 'Failed to save assessment. Please try again.');
            } finally {
                setSubmittingForm(false);
            }
        }
    };

    const clearForm = () => {
        setNewOption('');
        setAssignedTo([]);
        setAssessmentType('');
        setDescription('');
        setReportDescription('');
        setAssessmentName('');
        setMaxScore(0);
        setStartDate('');
        setEndDate('');
        setTimeLimit(0);
        setInterpretations([]);
        setRecommendations([]);
        setFormId(new Date().getTime());
        setQuestions([{ id: new Date().getTime().toString(), question: '', options: [], notes: '', optionType: 'Radio Button' }]);
    }

    const getDropDowns = async () => {
        try {
            const dropdownData = await getDropdownData();
            setDropdownAssessmentTypes(dropdownData.filter(item => item.type === "assessment-type").map(item => item.name));
            setDropdownProgramCodes(dropdownData.filter(item => item.type === "program_code").map(item => item.name));
        } catch (error) {
            console.log('An error occurred while fetching dropdown data.', error);
        }
    };

    const descriptionChange = useCallback((data) => {
        setDescription(data);
    }, []);

    const reportDescriptionChange = useCallback((data) => {
        setReportDescription(data);
    }, []);

    useEffect(() => {
        getDropDowns();
    }, []);

    // Sync option arrays with questions length
    useEffect(() => {
        setOptionValues(Array(questions.length).fill(''));
        setOptionScores(Array(questions.length).fill(''));
    }, [questions.length]);

    const handleInterpretations = (data) => {
        setInterpretations(data);
    }

    const handleRecommendations = (data) => {
        setRecommendations(data);
    }

    const handleCloseSuccess = () => {
        setSuccessMessage('');
    };

    useEffect(() => {
        if (assessment) {
            console.log('Assessment object received:', assessment);
            console.log('Assessment keys:', Object.keys(assessment));
            console.log('Assessment max_score field:', assessment.max_score);
            console.log('Assessment json field:', assessment.json);
            
            setAssessmentName(assessment.assignment_name);
            setAssessmentType(assessment.assignment_type);
            setInterpretations(assessment.interpretations);
            setRecommendations(assessment.recommendations);
            setAssignedTo(assessment.program_code.split(","));
            
            // Handle backward compatibility - read from JSON field
            let maxScoreValue = 0;
            let startDateValue = '';
            let endDateValue = '';
            let timeLimitValue = 0;
            let reportDescriptionValue = '';
            
            // First try to get from existing max_score field for backward compatibility
            if (assessment.max_score !== undefined && assessment.max_score !== null) {
                maxScoreValue = parseInt(assessment.max_score) || 0;
                console.log('Found max_score in assessment.max_score:', maxScoreValue);
            }
            
            // Then try to get from JSON field (newer approach)
            if (assessment.json) {
                try {
                    const jsonData = typeof assessment.json === 'string' ? JSON.parse(assessment.json) : assessment.json;
                    console.log('Parsed JSON data:', jsonData);
                    if (jsonData.max_score !== undefined && jsonData.max_score !== null) {
                        maxScoreValue = parseInt(jsonData.max_score) || 0;
                        console.log('Found max_score in JSON:', maxScoreValue);
                    }
                    if (jsonData.start_date !== undefined) {
                        startDateValue = jsonData.start_date || '';
                        console.log('Found start_date in JSON:', startDateValue);
                    }
                    if (jsonData.end_date !== undefined) {
                        endDateValue = jsonData.end_date || '';
                        console.log('Found end_date in JSON:', endDateValue);
                    }
                    if (jsonData.time_limit !== undefined && jsonData.time_limit !== null) {
                        timeLimitValue = parseInt(jsonData.time_limit) || 0;
                        console.log('Found time_limit in JSON:', timeLimitValue);
                    }
                    if (jsonData.report_description !== undefined) {
                        reportDescriptionValue = jsonData.report_description || '';
                        console.log('Found report_description in JSON:', reportDescriptionValue);
                    }
                } catch (error) {
                    console.log('Error parsing JSON for assessment data:', error);
                }
            }
            
            console.log('Final assessment values being set:', { maxScoreValue, startDateValue, endDateValue, timeLimitValue, reportDescriptionValue });
            setMaxScore(maxScoreValue);
            setStartDate(startDateValue);
            setEndDate(endDateValue);
            setTimeLimit(timeLimitValue);
            
            console.log('Setting report description:', reportDescriptionValue);
            setReportDescription(reportDescriptionValue);
            
            let selectedQuestions = [];
            assessment.questions.forEach(item => {
                let obj = {
                    id: item.id,
                    question: item.question,
                    options: item.options,
                    optionType: item.optionType,
                    notes: item.notes
                };
                selectedQuestions.push(obj);
            })
            setQuestions(selectedQuestions);
        }
    }, [assessment])

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
            <Paper
                elevation={2}
                sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    minHeight: '100vh'
                }}
            >
                {/* Header Section */}
                <Box
                    sx={{
                        // background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                        background: '#0a0068',
                        color: 'white',
                        p: { xs: 2, md: 3 },
                        borderBottom: '4px solid #ff6b6b'
                    }}
                >
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        fontWeight="bold"
                        sx={{
                            textAlign: 'center',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                            fontSize: { xs: '1.1rem', md: '1.3rem' },
                            fontFamily: 'Poppins'
                        }}
                    >
                        📋 Assessment Builder
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                            mt: 1,
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontFamily: 'Poppins'
                        }}
                    >
                        Create and manage comprehensive assessments with ease
                    </Typography>
                </Box>

                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    {/* Assessment Details Card */}
                    <Card
                        elevation={3}
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3,
                                    borderLeft: '4px solid #ff6b6b',
                                    pl: 2
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    color="#2c3e50"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem', fontFamily: 'Poppins' }}
                                >
                                    📝 Assessment Details
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {/* First row */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        id="name"
                                        label="Assessment Name"
                                        variant="outlined"
                                        fullWidth
                                        value={assessmentName}
                                        onChange={(e) => setAssessmentName(e.target.value)}
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    transform: 'scale(1.01)'
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        value={assessmentType}
                                        onChange={(e, newValue) => handleAssessmentTypeChange(newValue)}
                                        options={dropdownAssessmentTypes}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label="Assessment Template"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        fontFamily: 'Poppins',
                                                        fontSize: '0.85rem',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                        }
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        fontFamily: 'Poppins',
                                                        fontSize: '0.85rem'
                                                    }
                                                }}
                                            />
                                        }
                                    />
                                </Grid>
                                
                                {/* Second row */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        id="max-score"
                                        label="Maximum Score*"
                                        placeholder="Enter maximum score"
                                        variant="outlined"
                                        fullWidth
                                        type="number"
                                        value={maxScore}
                                        onChange={(e) => setMaxScore(e.target.value)}
                                        inputProps={{ min: 1 }}
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    transform: 'scale(1.01)'
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        multiple
                                        value={assignedTo}
                                        onChange={(e, newValue) => handleAssignedToChange(newValue)}
                                        options={dropdownProgramCodes}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="filled"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                    sx={{
                                                        background: 'linear-gradient(45deg, #0a0068, #cc4049)',
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            ))
                                        }
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label="Assigned To"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        fontFamily: 'Poppins',
                                                        fontSize: '0.85rem',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                        }
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        fontFamily: 'Poppins',
                                                        fontSize: '0.85rem'
                                                    }
                                                }}
                                            />
                                        }
                                    />
                                </Grid>
                                
                                {/* Third row */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        id="start-date"
                                        label="Start Date*"
                                        variant="outlined"
                                        fullWidth
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    transform: 'scale(1.01)'
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        id="end-date"
                                        label="End Date*"
                                        variant="outlined"
                                        fullWidth
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    transform: 'scale(1.01)'
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins'
                                            }
                                        }}
                                    />
                                </Grid>
                                
                                {/* Fourth row */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        id="time-limit"
                                        label="Time Limit (minutes)"
                                        placeholder="0 (unlimited)"
                                        variant="outlined"
                                        fullWidth
                                        type="number"
                                        value={timeLimit}
                                        onChange={(e) => setTimeLimit(e.target.value)}
                                        inputProps={{ min: 0 }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    transform: 'scale(1.01)'
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.85rem',
                                                fontFamily: 'Poppins'
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Instructions Card */}
                    <Card
                        elevation={3}
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                    borderLeft: '4px solid #0a0068',
                                    pl: 2
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    color="#2c3e50"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem', fontFamily: 'Poppins' }}
                                >
                                    📋 Assessment Instructions
                                </Typography>
                            </Box>
                            <DraftEditor
                                key={`instructions-${assessment?.id || 'new'}-${description.length}`}
                                handleDescription={descriptionChange}
                                description={description}
                                clearEditor={!description}
                                assessment={assessment}
                            />
                        </CardContent>
                    </Card>

                    {/* Report Description Card */}
                    <Card
                        elevation={3}
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                    borderLeft: '4px solid #cc4049',
                                    pl: 2
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    color="#2c3e50"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem', fontFamily: 'Poppins' }}
                                >
                                    📄 Report Description
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2, fontSize: '0.8rem', fontFamily: 'Poppins' }}
                            >
                                This description will appear in dashboard reports to provide context about the assessment.
                            </Typography>
                            <DraftEditor
                                key={`report-description-${assessment?.id || 'new'}-${reportDescription.length}`}
                                handleDescription={reportDescriptionChange}
                                description={reportDescription}
                                clearEditor={!reportDescription}
                                assessment={assessment ? { ...assessment, instructions: reportDescription } : null}
                            />
                        </CardContent>
                    </Card>

                    {/* Tabs Section */}
                    <Card
                        elevation={3}
                        sx={{
                            borderRadius: 2,
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{
                            // background: 'linear-gradient(90deg, #0a0068 0%, #cc4049 100%)',
                            background: '#0a0068',
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                aria-label="assessment sections"
                                variant={isMobile ? "fullWidth" : "standard"}
                                sx={{
                                    '& .MuiTab-root': {
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        fontFamily: 'Poppins',
                                        textTransform: 'none',
                                        minHeight: 48,
                                        letterSpacing: '0.2px',
                                        '&.Mui-selected': {
                                            color: 'white',
                                            fontWeight: 600
                                        },
                                        '&:hover': {
                                            color: 'rgba(255, 255, 255, 0.9)'
                                        }
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: '#6B7280',
                                        height: 2
                                    }
                                }}
                            >
                                <Tab label="❓ Questions" />
                                <Tab label="📊 Interpretations" />
                                <Tab label="💡 Recommendations" />
                            </Tabs>
                        </Box>

                        <TabPanel value={activeTab} index={0}>
                            {/* Questions Tab Content */}
                            <Stack spacing={3}>

                                {/* Questions List */}
                                <Stack spacing={3}>
                                    {questions.map((question, index) => (
                                        <Fade in={true} timeout={300 * (index + 1)} key={question.id}>
                                            <Card
                                                elevation={2}
                                                sx={{
                                                    borderRadius: 2,
                                                    border: '2px solid transparent',
                                                    background: 'linear-gradient(white, white) padding-box, linear-gradient(45deg, #ff6b6b, #4ecdc4) border-box',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)'
                                                    }
                                                }}
                                            >
                                                <CardContent>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        mb: 2
                                                    }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight="bold"
                                                            color="#2c3e50"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                fontSize: '0.9rem',
                                                                fontFamily: 'Poppins'
                                                            }}
                                                        >
                                                            <Chip
                                                                label={index + 1}
                                                                size="small"
                                                                sx={{
                                                                    background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.7rem',
                                                                    height: '20px'
                                                                }}
                                                            />
                                                            Question {index + 1}
                                                        </Typography>
                                                        <IconButton
                                                            onClick={() => handleDeleteQuestion(index)}
                                                            aria-label="delete"
                                                            size="small"
                                                            sx={{
                                                                color: '#e74c3c',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>

                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                id="question"
                                                                label="Question"
                                                                variant="outlined"
                                                                fullWidth
                                                                multiline
                                                                rows={2}
                                                                value={question.question}
                                                                onChange={(e) => handleQuestionChange(index, e.target.value)}
                                                                required
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s',
                                                                        '&:hover': {
                                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Autocomplete
                                                                multiple
                                                                value={question.options}
                                                                onChange={(e, newValues) => handleOptionChange(index, newValues)}
                                                                options={dropdownOptions}
                                                                renderTags={(value, getTagProps) =>
                                                                    value.map((option, optionIndex) => (
                                                                        <Chip
                                                                            variant="filled"
                                                                            label={option}
                                                                            {...getTagProps({ index: optionIndex })}
                                                                            sx={{
                                                                                background: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
                                                                                color: 'white',
                                                                                fontWeight: 'bold',
                                                                                m: 0.5
                                                                            }}
                                                                        />
                                                                    ))
                                                                }
                                                                renderInput={(params) =>
                                                                    <TextField
                                                                        {...params}
                                                                        label="Options*"
                                                                        fullWidth
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                borderRadius: 2,
                                                                                transition: 'all 0.3s',
                                                                                '&:hover': {
                                                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                }
                                                            />

                                                            <Box sx={{ mt: 2 }}>
                                                                {question.optionType === 'Text Field' ? (
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        backgroundColor: '#f5f5f5', 
                                                                        borderRadius: 2, 
                                                                        border: '1px dashed #ccc',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontFamily: 'Poppins' }}>
                                                                            Options are not required for Text Field questions
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '0.65rem', fontFamily: 'Poppins' }}>
                                                                            Users will be presented with a text area to enter their answers
                                                                        </Typography>
                                                                    </Box>
                                                                ) : (
                                                                    <Grid container spacing={1}>
                                                                        <Grid item xs={12} sm={6}>
                                                                            <TextField
                                                                                id="options"
                                                                                label="Option"
                                                                                variant="outlined"
                                                                                fullWidth
                                                                                size="small"
                                                                                value={optionValues[index]}
                                                                                onChange={(e) => handleOptionValueChange(index, e.target.value)}
                                                                                sx={{
                                                                                    '& .MuiOutlinedInput-root': {
                                                                                        borderRadius: 2
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={3}>
                                                                            <TextField
                                                                                id="score"
                                                                                label="Score"
                                                                                placeholder="0 (default)"
                                                                                variant="outlined"
                                                                                fullWidth
                                                                                size="small"
                                                                                type="number"
                                                                                value={optionScores[index]}
                                                                                onChange={(e) => handleOptionScoreChange(index, e.target.value)}
                                                                                sx={{
                                                                                    '& .MuiOutlinedInput-root': {
                                                                                        borderRadius: 2
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={3}>
                                                                            <Button
                                                                                onClick={() => handleAddOption(index)}
                                                                                variant="contained"
                                                                                size="small"
                                                                                fullWidth
                                                                                sx={{
                                                                                    // background: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
                                                                                    background: "#0a0068",
                                                                                    borderRadius: 2,
                                                                                    fontWeight: 'bold',
                                                                                    textTransform: 'none',
                                                                                    height: '32px',
                                                                                    fontSize: '0.75rem',
                                                                                    '&:hover': {
                                                                                        background: 'linear-gradient(45deg, #44a08d, #396f67)'
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Add
                                                                            </Button>
                                                                        </Grid>
                                                                    </Grid>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    <Grid container spacing={2} sx={{ mt: 2 }}>
                                                        <Grid item xs={12} md={6}>
                                                            <Autocomplete
                                                                value={question.optionType}
                                                                onChange={(e, newValue) => handleOptionTypeChange(index, newValue)}
                                                                options={['Radio Button', 'Checkbox', 'Text Field']}
                                                                renderInput={(params) =>
                                                                    <TextField
                                                                        {...params}
                                                                        label="Option Type"
                                                                        fullWidth
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                borderRadius: 2,
                                                                                transition: 'all 0.3s',
                                                                                '&:hover': {
                                                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                multiline
                                                                minRows={3}
                                                                maxRows={6}
                                                                placeholder="Instructions If Any..."
                                                                value={question.notes}
                                                                onChange={(e) => handleNotesChange(index, e.target.value)}
                                                                fullWidth
                                                                variant="outlined"
                                                                label="Notes"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.3s',
                                                                        '&:hover': {
                                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Fade>
                                    ))}
                                    <Box sx={{ display: 'flex', justifyContent: 'right' }}>
                                        <Button
                                            onClick={handleAddQuestion}
                                            variant="contained"
                                            startIcon={<AddIcon fontSize="small" />}
                                            size="medium"
                                            sx={{
                                                background: 'linear-gradient(45deg, #0a0068, #cc4049)',
                                                borderRadius: 3,
                                                px: 3,
                                                py: 1,
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                fontSize: '0.8rem',
                                                '&:hover': {
                                                    background: 'linear-gradient(45deg, #5a67d8, #6b5b95)',
                                                    transform: 'translateY(-2px)'
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Add Question
                                        </Button>
                                    </Box>

                                </Stack>
                            </Stack>
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            <Box sx={{
                                background: 'rgba(255, 255, 255, 0.5)',
                                borderRadius: 2,
                                p: 2
                            }}>
                                <Interpretations
                                    updateInterpretations={handleInterpretations}
                                    interpretations={interpretations}
                                />
                            </Box>
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            <Box sx={{
                                background: 'rgba(255, 255, 255, 0.5)',
                                borderRadius: 2,
                                p: 2
                            }}>
                                <Recommendations
                                    updateRecommendations={handleRecommendations}
                                    recommendations={recommendations}
                                />
                            </Box>
                        </TabPanel>
                    </Card>

                    {/* Submit Button */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 4,
                        mb: 2
                    }}>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            size="medium"
                            disabled={submittingForm}
                            sx={{
                                background: submittingForm
                                    ? 'linear-gradient(45deg, #95a5a6, #7f8c8d)'
                                    : 'linear-gradient(45deg, #0a0068, #cc4049)',
                                borderRadius: 3,
                                px: { xs: 3, md: 4 },
                                py: { xs: 1, md: 1.2 },
                                fontSize: { xs: '0.85rem', md: '0.9rem' },
                                fontWeight: 'bold',
                                textTransform: 'none',
                                '&:hover': !submittingForm && {
                                    background: 'linear-gradient(45deg, #5a67d8, #6b5b95)',
                                    transform: 'translateY(-2px)'
                                },
                                '&:disabled': {
                                    color: 'white'
                                },
                                transition: 'all 0.3s ease',
                                minWidth: { xs: 180, md: 200 }
                            }}
                        >
                            {submittingForm ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            borderTop: '2px solid white',
                                            animation: 'spin 1s linear infinite',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' }
                                            }
                                        }}
                                    />
                                    Submitting...
                                </Box>
                            ) : (
                                '🚀 Submit Assessment'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Popup for adding new option */}
            <Dialog
                open={showPopup}
                onClose={() => setShowPopup(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                        color: 'white',
                        fontWeight: 'bold',
                        borderBottom: '4px solid #ff6b6b',
                        p: 3
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.9rem', fontFamily: 'Poppins' }}>
                            ➕ Add Master Data
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowPopup(false)}
                        sx={{
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 2 }}>
                    <Stack spacing={3}>
                        <TextField
                            select
                            label="Data Type"
                            fullWidth
                            value={dataType}
                            onChange={handleDataTypeChange}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }
                                }
                            }}
                        >
                            <MenuItem value="assessment-question">Question</MenuItem>
                            <MenuItem value="assessment-option">Answer</MenuItem>
                            <MenuItem value="assessment-type">Assessment Template</MenuItem>
                            <MenuItem value="program_code">Assigned To</MenuItem>
                        </TextField>

                        <TextField
                            label="Answer"
                            fullWidth
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }
                                }
                            }}
                        />

                        {dataType === "assessment-option" && (
                            <TextField
                                label="Score"
                                fullWidth
                                type="number"
                                value={score}
                                onChange={(e) => setScore(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }
                                    }
                                }}
                            />
                        )}

                        <Button
                            onClick={handlePopupSubmit}
                            variant="contained"
                            size="medium"
                            disabled={submittingMasterData}
                            sx={{
                                background: submittingMasterData
                                    ? 'linear-gradient(45deg, #95a5a6, #7f8c8d)'
                                    : 'linear-gradient(45deg, #0a0068, #cc4049)',
                                borderRadius: 2,
                                py: 1.2,
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                '&:hover': !submittingMasterData && {
                                    background: 'linear-gradient(45deg, #5a67d8, #6b5b95)'
                                },
                                '&:disabled': {
                                    color: 'white'
                                }
                            }}
                        >
                            {submittingMasterData ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: '50%',
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            borderTop: '2px solid white',
                                            animation: 'spin 1s linear infinite',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' }
                                            }
                                        }}
                                    />
                                    Submitting...
                                </Box>
                            ) : (
                                'Submit'
                            )}
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={Boolean(successMessage)}
                autoHideDuration={6000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                TransitionComponent={Slide}
            >
                <Alert
                    onClose={handleCloseSuccess}
                    severity="success"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        fontWeight: 'bold',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        }
                    }}
                    variant="filled"
                >
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar
                open={Boolean(errorMessage)}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                TransitionComponent={Slide}
            >
                <Alert
                    onClose={handleCloseError}
                    severity="error"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        fontWeight: 'bold',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        }
                    }}
                    variant="filled"
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

// TabPanel component
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default AssessmentForm;